
import { supabase } from '@/integrations/supabase/client';
import { LearningModule, TaxonomyLevel } from '@/types/supabase';

/**
 * Fetch all modules
 */
export const fetchModules = async (): Promise<LearningModule[]> => {
  try {
    const { data, error } = await supabase
      .from('learning_modules')
      .select('*')
      .order('order_position', { ascending: true });

    if (error) {
      throw error;
    }

    // Cast the taxonomy_level property to ensure it matches the expected type
    return (data || []).map(module => ({
      ...module,
      taxonomy_level: module.taxonomy_level as TaxonomyLevel[] || null,
      phase: module.phase as LearningModule['phase'] || null,
      track: module.track as LearningModule['track'] || null
    }));
  } catch (error) {
    console.error('Error fetching modules:', error);
    throw error;
  }
};

/**
 * Fetch a module by ID
 */
export const fetchModuleById = async (moduleId: string): Promise<LearningModule | null> => {
  try {
    const { data, error } = await supabase
      .from('learning_modules')
      .select('*')
      .eq('id', moduleId)
      .single();

    if (error) {
      throw error;
    }

    // Cast the taxonomy_level property to ensure it matches the expected type
    return data ? {
      ...data,
      taxonomy_level: data.taxonomy_level as TaxonomyLevel[] || null,
      phase: data.phase as LearningModule['phase'] || null,
      track: data.track as LearningModule['track'] || null
    } : null;
  } catch (error) {
    console.error('Error fetching module:', error);
    throw error;
  }
};

/**
 * Create a new module
 */
export const createModule = async (module: Omit<LearningModule, 'id' | 'created_at' | 'updated_at'>): Promise<LearningModule> => {
  try {
    const { data, error } = await supabase
      .from('learning_modules')
      .insert(module)
      .select()
      .single();

    if (error) {
      throw error;
    }

    // Cast the taxonomy_level property to ensure it matches the expected type
    return {
      ...data,
      taxonomy_level: data.taxonomy_level as TaxonomyLevel[] || null,
      phase: data.phase as LearningModule['phase'] || null,
      track: data.track as LearningModule['track'] || null
    };
  } catch (error) {
    console.error('Error creating module:', error);
    throw error;
  }
};

/**
 * Update an existing module
 */
export const updateModule = async (moduleId: string, updates: Partial<LearningModule>): Promise<LearningModule> => {
  try {
    const { data, error } = await supabase
      .from('learning_modules')
      .update(updates)
      .eq('id', moduleId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    // Cast the taxonomy_level property to ensure it matches the expected type
    return {
      ...data,
      taxonomy_level: data.taxonomy_level as TaxonomyLevel[] || null,
      phase: data.phase as LearningModule['phase'] || null,
      track: data.track as LearningModule['track'] || null
    };
  } catch (error) {
    console.error('Error updating module:', error);
    throw error;
  }
};

/**
 * Delete a module
 */
export const deleteModule = async (moduleId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('learning_modules')
      .delete()
      .eq('id', moduleId);

    if (error) {
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error deleting module:', error);
    throw error;
  }
};

/**
 * Sync module taxonomy levels with objective taxonomy levels
 */
export const syncModuleTaxonomyLevels = async (moduleId: string): Promise<LearningModule | null> => {
  try {
    // 1. Fetch learning objectives for the module
    const { data: objectives, error: objectivesError } = await supabase
      .from('learning_objectives')
      .select('taxonomy_level')
      .eq('module_id', moduleId);

    if (objectivesError) {
      throw objectivesError;
    }

    // If no objectives are found, don't update the module
    if (!objectives || objectives.length === 0) {
      return fetchModuleById(moduleId);
    }

    // 2. Extract unique taxonomy levels from objectives
    const taxonomyLevels = [...new Set(objectives.map(obj => obj.taxonomy_level))] as TaxonomyLevel[];

    // 3. Update the module with these taxonomy levels
    const { data, error } = await supabase
      .from('learning_modules')
      .update({ taxonomy_level: taxonomyLevels })
      .eq('id', moduleId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    // Return updated module
    return {
      ...data,
      taxonomy_level: data.taxonomy_level as TaxonomyLevel[] || null,
      phase: data.phase as LearningModule['phase'] || null,
      track: data.track as LearningModule['track'] || null
    };
  } catch (error) {
    console.error('Error syncing module taxonomy levels:', error);
    return null;
  }
};
